import { NavLink } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { Settings2, ScrollText, UserCheck, BookOpen, LineChart, CalendarClock, Users, Scale, FileSpreadsheet, ListChecks, Wallet, StickyNote, Landmark, FileBarChart, Sun, Moon, Receipt, LayoutDashboard } from "lucide-react";
import { useMe } from "@/components/AccessGate";
import { useTheme } from "@/lib/theme";
import twpLogo from "@/assets/twp-logo.png";

const NAV = [
  { items: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "sidebar-nav-dashboard" },
    { to: "/ledger", label: "Ledger", icon: BookOpen, id: "sidebar-nav-ledger" },
    { to: "/invoicing", label: "Invoicing", icon: Receipt, id: "sidebar-nav-invoicing" },
  ] },
  { title: "Cashflow & reports", items: [
    { to: "/cashflow", label: "Cashflow", icon: LineChart, id: "sidebar-nav-cashflow" },
    { to: "/forecast", label: "Forecast", icon: CalendarClock, id: "sidebar-nav-forecast" },
    { to: "/profit-loss", label: "Profit & loss", icon: FileBarChart, id: "sidebar-nav-profit-loss" },
  ] },
  { title: "Directors", items: [
    { to: "/director-accounts", label: "Director accounts", icon: Users, id: "sidebar-nav-director-accounts" },
    { to: "/misc-accounts", label: "Misc & loans", icon: Wallet, id: "sidebar-nav-misc-accounts" },
    { to: "/memo-notes", label: "Memo notes", icon: StickyNote, id: "sidebar-nav-memo-notes" },
    { to: "/settlement", label: "Settlement", icon: Scale, id: "sidebar-nav-settlement" },
  ] },
  { title: "Imports", items: [
    { to: "/bank-feed", label: "Bank feed", icon: Landmark, id: "sidebar-nav-bank-feed" },
    { to: "/import-export", label: "Import & export", icon: FileSpreadsheet, id: "sidebar-nav-import-export" },
    { to: "/data-quality", label: "Data quality", icon: ListChecks, id: "sidebar-nav-data-quality" },
  ] },
  { title: "Settings", items: [
    { to: "/setup", label: "Setup", icon: Settings2, id: "sidebar-nav-setup" },
    { to: "/audit-log", label: "Audit log", icon: ScrollText, id: "sidebar-nav-audit-log" },
    { to: "/access", label: "Admin", icon: UserCheck, id: "sidebar-nav-access" },
  ] },
];

const NavItem = ({ to, label, icon: Icon, id }) => (
  <NavLink
    to={to}
    data-testid={id}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-1.5 text-sm rounded-xl whitespace-nowrap transition-colors duration-150 ${
        isActive ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground hover:text-foreground hover:bg-accent"
      }`
    }
  >
    <Icon className="h-4 w-4" /> <span>{label}</span>
  </NavLink>
);

const ThemeToggle = () => {
  const { theme, toggle } = useTheme();
  return (
    <button type="button" onClick={toggle} data-testid="theme-toggle" aria-label="Switch light or dark mode"
      className="ml-auto md:ml-0 h-9 w-9 shrink-0 grid place-items-center rounded-full border border-border bg-background text-muted-foreground hover:text-primary hover:rotate-12 transition-transform duration-200">
      {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
};

export default function AppShell({ children }) {
  const me = useMe();
  return (
    <div className="min-h-screen md:flex">
      <aside className="md:w-60 md:fixed md:inset-y-0 border-b md:border-b-0 md:border-r border-border bg-card flex md:flex-col">
        <div className="p-5 md:p-6 flex items-start gap-3">
          <div className="min-w-0">
            <img src={twpLogo} alt="TWP — The White Project Co." className="h-6 md:h-7 w-auto" data-testid="sidebar-logo" />
            <div className="font-display text-sm font-semibold mt-2" data-testid="app-title">Financial Records</div>
          </div>
          <div className="hidden md:block ml-auto"><ThemeToggle /></div>
        </div>
        <div className="md:hidden flex items-center pr-3"><ThemeToggle /></div>
        <nav className="flex md:flex-col gap-0.5 px-2 md:px-3 pb-2 items-center md:items-stretch overflow-x-auto md:overflow-y-auto">
          {NAV.map((g, i) => (
            <div key={i} className="flex md:flex-col gap-0.5 items-center md:items-stretch md:mt-3 first:md:mt-0" data-testid={`sidebar-group-${i}`}>
              {g.title && <div className="hidden md:block px-3 pt-1 pb-1 text-[10px] uppercase tracking-[0.16em] text-muted-foreground/80 font-semibold">{g.title}</div>}
              {g.title && <span className="md:hidden h-5 w-px bg-border mx-1 shrink-0" />}
              {g.items.map((n) => <NavItem key={n.to} {...n} />)}
            </div>
          ))}
        </nav>
        <div className="hidden md:flex mt-auto p-5 items-center gap-3 border-t border-border">
          <UserButton />
          <div className="min-w-0">
            <div className="text-sm truncate" data-testid="current-user-name">{me?.name}</div>
            <div className="text-xs text-muted-foreground truncate font-mono">{me?.email}</div>
          </div>
        </div>
      </aside>
      <main className="md:ml-60 flex-1 p-5 md:p-8 lg:p-10 min-w-0">{children}</main>
    </div>
  );
}
